import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, TrendingUp, AlertTriangle, BarChart3, Banknote, Shield, Building, Heart } from 'lucide-react';
import { getSpecializationConfigs, getCategoryTitle } from '../config/fundConfigurations';
import MultipleComparisonTables, { CompactComparisonTable } from '../components/CompactComparisonTable';
import Header from '../components/Header';

const CategoryPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // קבלת הקטגוריה שממנה הגענו
  const fromCategory = searchParams.get('from');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');
  const [topProducts, setTopProducts] = useState([]);
  const [statistics, setStatistics] = useState(null);
  
  // קבלת פרטי הקטגוריה
  const categoryTitle = getCategoryTitle(categoryId);
  const categoryConfig = getCategoryInfo(categoryId);

  // פונקציה לקבלת מידע על הקטגוריה
  function getCategoryInfo(categoryId) {
    const categoryInfos = {
      'israeli-stocks': {
        title: 'מסלולים ישראלים - מניות ישראל בלבד',
        emoji: '🔥',
        description: 'מציג מסלולים מניותיים שכל ההשקעה בהם היא במניות של חברות ישראליות בלבד בשקלים - ללא חשיפה לשווקים זרים או למטבע חוץ.',
        riskLevel: 5,
        riskDescription: 'רמת סיכון: 5 מתוך 5',
        color: 'from-red-500 to-orange-500',
        bgColor: 'from-red-50 to-orange-50',
        borderColor: 'border-red-200'
      },
      'foreign-stocks': {
        title: 'מסלולים מניותיים - חו"ל בלבד',
        emoji: '🌍',
        description: 'מסלולים שמושקעים ב-100% מניות חו"ל, כגון S&P 500, נאסד"ק, MSCI World ללא השקעה בחברות ישראליות.',
        riskLevel: 5,
        riskDescription: 'רמת סיכון: 5 מתוך 5',
        color: 'from-blue-500 to-indigo-500',
        bgColor: 'from-blue-50 to-indigo-50',
        borderColor: 'border-blue-200'
      },
      'mixed-global': {
        title: 'מבולבלים כלליים - פיזור גלובלי',
        emoji: '🌐',
        description: 'מסלולים מגוונים שמשלבים מניות, אג"ח ונכסים נוספים - הן מישראל והן מהעולם - תוך שמירה על איזון בין סיכון לתשואה.',
        riskLevel: 3,
        riskDescription: 'רמת סיכון: 3 מתוך 5',
        color: 'from-green-500 to-teal-500',
        bgColor: 'from-green-50 to-teal-50',
        borderColor: 'border-green-200'
      },
      'stocks-global': {
        title: 'מסלולים מניותיים - פיזור גלובלי',
        emoji: '📈',
        description: 'מסלולים שמפוזרים גאוגרפית ומשלבים מניות ממדינות שונות. מאופיינים בניהול אקטיבי ודינמי בניגוד לגישה הפסיבית.',
        riskLevel: 5,
        riskDescription: 'רמת סיכון: 5 מתוך 5',
        color: 'from-purple-500 to-pink-500',
        bgColor: 'from-purple-50 to-pink-50',
        borderColor: 'border-purple-200'
      },
      'solid-shekel': {
        title: 'מסלולים סולידיים - פקדונות ושקליים בלבד',
        emoji: '💰',
        description: 'מסלולים ללא מניות כלל - השקעות סולידיות בשקלים, כולל פקדונות, אג"ח ממשלתיות וחברות, מקמים והלוואות שקליות. מתאימים למשקיעים זהירים בטווח השקעה קצר.',
        riskLevel: 1,
        riskDescription: 'רמת סיכון: 1 מתוך 5',
        color: 'from-yellow-500 to-amber-500',
        bgColor: 'from-yellow-50 to-amber-50',
        borderColor: 'border-yellow-200'
      }
    };

    return categoryInfos[categoryId] || {
      title: categoryId,
      emoji: '📊',
      description: 'קטגוריה לא ידועה',
      riskLevel: 3,
      riskDescription: 'רמת סיכון: לא ידועה',
      color: 'from-gray-500 to-gray-600',
      bgColor: 'from-gray-50 to-gray-100',
      borderColor: 'border-gray-200'
    };
  }

  // פונקציה לקבלת כוכבי סיכון
  const getRiskStars = (level) => {
    return '⭐'.repeat(level) + '☆'.repeat(5 - level);
  };

  // פונקציה לחזרה לדף הבית
  const goToHomePage = () => {
    navigate('/');
  };

  // פונקציה ריקה לטיפול בחיפוש
  const handleSearchResults = (results, searchTerm, error = null) => {
    setCurrentSearchTerm(searchTerm || '');
    setTopProducts(results || []);
    setError(error);
  };

  // עדכון טייטל הדף
  useEffect(() => {
    document.title = `${categoryConfig.title} | מערכת השוואת מוצרי חיסכון`;
  }, [categoryConfig.title]);

  // פונקציה לקבלת נתונים על קטגוריות המוצרים
  const getProductCategories = () => {
    return [
      {
        id: 'study-funds',
        title: 'קרנות השתלמות',
        icon: BarChart3,
        color: 'from-blue-500 to-blue-600',
        bgColor: 'from-blue-50 to-indigo-50',
        borderColor: 'border-blue-200'
      },
      {
        id: 'pension-savings',
        title: 'קופות גמל',
        icon: Banknote,
        color: 'from-green-500 to-green-600',
        bgColor: 'from-green-50 to-emerald-50',
        borderColor: 'border-green-200'
      },
      {
        id: 'investment-funds',
        title: 'קופות גמל להשקעה',
        icon: TrendingUp,
        color: 'from-purple-500 to-purple-600',
        bgColor: 'from-purple-50 to-violet-50',
        borderColor: 'border-purple-200'
      },
      {
        id: 'insurance-savings',
        title: 'פוליסות חיסכון',
        icon: Shield,
        color: 'from-orange-500 to-orange-600',
        bgColor: 'from-orange-50 to-amber-50',
        borderColor: 'border-orange-200'
      },
      {
        id: 'pension-plans',
        title: 'קרנות פנסיה',
        icon: Building,
        color: 'from-red-500 to-red-600',
        bgColor: 'from-red-50 to-rose-50',
        borderColor: 'border-red-200'
      },
      {
        id: 'child-savings',
        title: 'תוכנית חיסכון לכל ילד',
        icon: Heart,
        color: 'from-pink-500 to-pink-600',
        bgColor: 'from-pink-50 to-rose-50',
        borderColor: 'border-pink-200'
      }
    ];
  };

  // פונקציה לסינון טבלאות לפי קטגוריה
 // פונקציה לסינון טבלאות לפי קטגוריה - פשוט ויעיל
const getFilteredTables = (productCategory) => {
  const specializations = getSpecializationConfigs(categoryId);
  
  return specializations.filter(spec => {
    // אם יש productType - השתמש בו
    if (spec.productType) {
      return spec.productType === productCategory;
    }
    
    // אם אין productType - לא להציג (כדי לא לקבל תוצאות לא רצויות)
    return false;
  });
};

  // בדיקה אם הקטגוריה קיימת
  if (!categoryConfig || !categoryId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">קטגוריה לא נמצאה</h1>
          <button
            onClick={goToHomePage}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            חזרה לדף הבית
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-8 md:pt-14" style={{ margin: 0, padding: 0 }}>
      {/* Header */}
      <Header 
        selectedCategory={null}
        currentCategory={null}
        savingsCategories={[]}
        goToHomePage={goToHomePage}
        handleCategoryChange={() => {}}
        handleSearchResults={handleSearchResults}
        loading={loading}
        currentSearchTerm={currentSearchTerm}
        statistics={statistics}
      />

      {/* כותרת הקטגוריה */}
      <section className={`py-12 bg-gradient-to-br ${categoryConfig.bgColor} border-b ${categoryConfig.borderColor}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* כפתור חזרה */}
          <div className="mb-6">
            <button
              onClick={goToHomePage}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowRight className="h-4 w-4" />
              חזרה לדף הבית
            </button>
          </div>

          {/* כותרת ראשית */}
          <div className="text-center">
            <div className="text-6xl mb-4">{categoryConfig.emoji}</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {categoryConfig.title}
            </h1>
            
            {/* תיאור */}
            <p className="text-lg text-gray-700 mb-6 max-w-4xl mx-auto leading-relaxed">
              {categoryConfig.description}
            </p>

            {/* רמת סיכון */}
            <div className="inline-flex items-center gap-4 bg-white/60 backdrop-blur-sm rounded-xl px-6 py-3 border border-white/20">
              <div className="text-2xl">{getRiskStars(categoryConfig.riskLevel)}</div>
              <div className="text-sm font-medium text-gray-700">
                {categoryConfig.riskDescription}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* טבלאות השוואה */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* כותרת סקציה */}
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              השוואת קרנות בקטגוריה
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {fromCategory ? 
                `מציג רק קרנות מסוג "${getCategoryTitle(fromCategory)}"` : 
                'השוואה בין כל סוגי המוצרים - קרנות השתלמות, קופות גמל, פנסיה, ביטוח ועוד'
              }
            </p>
          </div>

          {/* טבלאות לפי קטגוריה ספציפית */}
          {fromCategory ? (
            <MultipleComparisonTables 
              category={categoryId}
              categoryTitle={categoryConfig.title}
              fromCategory={fromCategory}
            />
          ) : (
            /* טבלאות לפי כל הקטגוריות */
            <div className="space-y-16">
              {getProductCategories().map((productCategory) => {
                const filteredTables = getFilteredTables(productCategory.id);
                
                if (filteredTables.length === 0) return null;
                
                const IconComponent = productCategory.icon;
                
                return (
                  <div key={productCategory.id} className={`bg-gradient-to-br ${productCategory.bgColor} rounded-2xl p-6 md:p-8 border ${productCategory.borderColor}`}>
                    
                    {/* כותרת קטגוריה */}
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`p-3 bg-gradient-to-r ${productCategory.color} rounded-xl shadow-md`}>
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl md:text-2xl font-bold text-gray-900">
                          {productCategory.title}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          {filteredTables.length} טבלאות השוואה זמינות
                        </p>
                      </div>
                    </div>

                    {/* טבלאות */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredTables.map((spec) => (
                        <CompactComparisonTable
                          key={spec.key}
                          category={categoryId}
                          specialization={spec.key}
                          title={spec.title}
                          fundIds={spec.fundIds}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* הצגת שגיאות */}
      {error && (
        <section className="py-8 bg-red-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-2 bg-red-100 text-red-800 rounded-lg">
                <AlertTriangle className="h-5 w-5 mr-2" />
                {error}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* מידע נוסף והמלצות */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* טיפים להשקעה */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                💡 טיפים להשקעה
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• השווה תשואות לפני דמי ניהול</li>
                <li>• בדוק את היסטוריית התשואות</li>
                <li>• שים לב לחשיפות הקרן</li>
                <li>• התייעץ עם יועץ מורשה</li>
              </ul>
            </div>

            {/* אזהרות סיכון */}
            <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                ⚠️ הערות חשובות
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• ביצועי עבר אינם מבטיחים תוצאות עתידיות</li>
                <li>• השקעה כרוכה בסיכונים</li>
                <li>• יש לקרוא את תשקיף הקרן</li>
                <li>• התאם את ההשקעה לפרופיל הסיכון שלך</li>
              </ul>
            </div>

            {/* מקורות נתונים */}
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                📊 מקורות הנתונים
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• גמל-נט - רשות שוק ההון</li>
                <li>• פנסיה-נט - משרד האוצר</li>
                <li>• ביטוח-נט - הממונה על הביטוח</li>
                <li>• עדכון נתונים יומי</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* קריאה לפעולה */}
      <section className={`py-12 bg-gradient-to-r ${categoryConfig.color} text-white`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold mb-4">רוצה לראות עוד קטגוריות?</h3>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            חזור לדף הבית כדי לראות את כל הקטגוריות הזמינות ולהשוות בין מאות קרנות
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={goToHomePage}
              className="px-8 py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl font-semibold transition-all duration-300 hover:scale-105 flex items-center gap-2"
            >
              <ArrowRight className="h-4 w-4" />
              חזרה לדף הבית
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CategoryPage;